<template>
	<div
		class="rich-text-box"
		:class="{ 'input-size-sm': inputSize === 'sm', 'is-single-line': isSingleLine, 'long-text': isLongText }"
		@mousedown="onMouseDown"
		@keypress="onKeyPress"
		@keyup.enter="onKeyPress"
		@keyup="onKeyUp"
	>
		<RichTextMenu v-if="isRichTextEnabled" :dontShowInsertData="dontShowInsertData" :editor="editor" :richTextFormat="richTextFormat" />
		<TemplateBubbleMenu :editor="editor" v-if="isTemplateMode" />
		<EditorContent :editor="editor" />
		<div class="suffix-icons" :style="{ right: `${padRight}px` }">
			<RoundButton v-if="hasDelete && validValue" @click="localValue = ''"><div class="close-x"></div></RoundButton>
		</div>
	</div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2';
import { mapGetters } from 'vuex';

import { replaceInsertDataStringDictionary } from '@/store/modules/selectedWorkflow/getters';
import { previousStepsData } from '@/store/modules/nodes/getters';
import { isRichTextSupportOn } from '@/store/modules/featureFlags/getters';

import TemplateBubbleMenu from './TemplateBubbleMenu/TemplateBubbleMenu';
import RichTextMenu from './RichTextMenu/RichTextMenu.vue';

import RichTextBoxService from './RichTextBoxService';
import ExtensionsService from './extensions/extensions-service';

import { TEXT_BOX_FORMAT_TYPES } from './rich-text-box.const';

import VModelMixin from '@/mixins/VModelMixin';
import { isNullOrUndefinedOrEmpty } from '@/utils/helpers';

export default {
	name: 'RichTextBox',
	mixins: [VModelMixin],
	components: {
		EditorContent,
		RichTextMenu,
		TemplateBubbleMenu,
	},
	props: {
		options: { type: Array },
		inputFieldHint: { type: [String, Number], default: '' },
		nodeId: { type: Number },
		isFocused: { type: Boolean },
		showDataMenu: { type: Boolean },
		multipleSelection: { type: Boolean },
		readonly: { type: Boolean, default: false },
		inputSize: { type: String },
		dataChipClass: { type: String },
		richTextFormat: { type: String },
		isSingleLine: { type: Boolean, default: false },
		paramKey: { type: String },
		isTemplateMode: { type: Boolean },
		isLongText: { type: Boolean },
		dontShowInsertData: { type: Boolean },
		disableEnter: { type: Boolean },
		hasDelete: { type: Boolean },
		padRight: { type: Number, default: 0 },
	},
	mounted() {
		const { isRichTextEnabled } = this;
		const content = this.localValue ? this.toTipTap(this.localValue) : '';

		const extensions = ExtensionsService.getBasicExtensions(isRichTextEnabled, this.inputFieldHint);
		if (isRichTextEnabled) {
			extensions.push(...ExtensionsService.getRichTextMenuExtensions());
		}
		this.editor = new Editor({
			editable: !this.readonly,
			content,
			extensions,
			editorProps: {
				attributes: {
					class: this.selectedClassActive ? 'selected' : '',
				},
				handleDOMEvents: {
					keydown: (view, event) => {
						if (event.key === 'Shift') {
							this.isShiftDown = true;
						}
						if (this.disableEnter && event.key === 'Backspace' && (this.localValue?.length || 0) < 1) {
							this.$emit('remove');
						}
						if (this.disableEnter && event.key === 'Enter') {
							if (this.isShiftDown) {
								this.editor.commands.insertContent('\n');
							}
							event.preventDefault();
							return true;
						}
						return false;
					},
				},
			},
			parseOptions: {
				preserveWhitespace: true,
			},
			onUpdate: ({ editor }) => {
				const { readonly, multipleSelection, isTemplateMode, richTextFormat } = this;
				const content = editor.getHTML();
				this.localValue = RichTextBoxService.fromTiptapFormat(content, readonly, multipleSelection, richTextFormat, isTemplateMode);
			},
			onFocus: () => {
				// old
				this.$emit('update:isFocused', true);
				// new
				this.$emit('focus');
			},
			onBlur: () => {
				// old
				this.$emit('update:isFocused', false);
				// new
				this.$emit('blur');
			},
			onTransaction: ({ editor }) => {
				this.anchorPosition = editor.state.selection.$anchor.pos;
			},
		});
	},
	watch: {
		localValue: {
			handler(newV) {
				if (this.editor && ![null, undefined].includes(newV)) {
					const nextV = this.toTipTap(newV);
					if (nextV !== this.editor.getHTML() && !this.editor.isFocused) {
						// This applies only to changes coming from outside (e.g. from insertData)
						const { anchorPosition: positionToReturnTo } = this;
						this.editor.commands.setContent(nextV); // order is important 1
						this.editor.commands.setTextSelection(positionToReturnTo); // order is important 2
					}
				}
			},
			immediate: true,
		},
		selectedClassActive: {
			handler(newV) {
				this.editor.setOptions({
					editorProps: {
						attributes: {
							class: newV ? 'selected' : '',
						},
					},
				});
			},
		},
		readonly: {
			handler(newV) {
				this.editor.setOptions({ editable: !newV });
			},
		},
	},
	beforeDestroy() {
		this.editor?.destroy();
	},
	methods: {
		toTipTap(content = this.localValue) {
			const { nodeId, options, dataChipConfig, showMultiSelectChips, paramKey, isTemplateMode, richTextFormat } = this;
			return RichTextBoxService.toTiptapFormat(
				content,
				nodeId,
				options,
				dataChipConfig,
				showMultiSelectChips,
				paramKey,
				isTemplateMode,
				this[previousStepsData.fullPath],
				richTextFormat,
				this[replaceInsertDataStringDictionary.fullPath]
			);
		},
		onKeyPress(e) {
			if (this.readonly) {
				if (['Enter', ' '].includes(e.key)) {
					this.onMouseDown();
				}
			}
			!this.isShiftDown && this.$emit('content-updated', e.key);
		},
		onKeyUp(e) {
			if (e.key === 'Shift') {
				this.isShiftDown = false;
			}
		},
		addValue(val, deleteLastChar) {
			deleteLastChar && this.editor.commands.deleteRange({ from: this.anchorPosition - 1, to: this.anchorPosition }); //NOTICE: ranges start from 1, not 0
			this.editor.commands.insertContent(val);
			this.editor.commands.focus();
		},
		onMouseDown() {
			this.$emit('click');
		},
		blur() {
			if (this.isFocused) {
				this.editor.commands.blur();
			}
		},
		focus() {
			this.editor.commands.focus();
		},
	},
	computed: {
		...mapGetters([previousStepsData.fullPath, isRichTextSupportOn.fullPath, replaceInsertDataStringDictionary.fullPath]),
		validValue() {
			return !isNullOrUndefinedOrEmpty(this.localValue);
		},
		dataChipConfig() {
			const configs = { isRichTextEnabled: this.isRichTextEnabled };
			if (this.dataChipClass) {
				configs.class = this.dataChipClass;
			}
			return configs;
		},
		isRichTextEnabled() {
			if (!this[isRichTextSupportOn.fullPath]) {
				return false;
			}
			return [TEXT_BOX_FORMAT_TYPES.HTML, TEXT_BOX_FORMAT_TYPES.MARKDOWN].includes(this.richTextFormat);
		},
		showMultiSelectChips() {
			return this.multipleSelection && this.readonly;
		},
		selectedClassActive() {
			return this.isFocused || this.showDataMenu;
		},
	},
	data() {
		return {
			anchorPosition: 1,
			editor: null,
			isShiftDown: false,
		};
	},
};
</script>

<style lang="scss">
.rich-text-box {
	position: relative;

	/* Basic editor styles */
	.ProseMirror {
		padding: 10px 8px;
		font-weight: 400;
		font-family: proxima-nova !important;
		border: 1px solid #e4e8f2;
		border-radius: 5px !important;
		min-height: 35px;

		> * + * {
			margin-top: 0.75em;
		}

		&.selected,
		&.active {
			border: var(--focused-border);
		}
		&:hover {
			background-color: var(--app-selection-hover-background);
		}

		// Placeholder
		p.is-editor-empty:first-child::before {
			content: attr(data-placeholder);
			float: left;
			color: #ced4da;
			pointer-events: none;
			height: 0;
		}

		::selection {
			background: var(--selected-text-font-color) !important;
		}
		&:focus-visible {
			outline: none;
		}
	}

	&.input-size-sm {
		.ProseMirror {
			height: 35px;
			min-height: 35px;
		}
	}

	&.long-text {
		.ProseMirror {
			min-height: 100px !important;
		}
	}

	&.is-single-line {
		.chip-sample {
			max-width: calc(100% - 40px);
			overflow: hidden;
			text-overflow: ellipsis;
			display: inline-block;
			white-space: nowrap;
			vertical-align: text-top;
		}
	}
	.ProseMirror.ProseMirror-selectednode {
		outline: 3px solid #68cef8;
	}
	img {
		max-width: 100%;
	}
	.suffix-icons {
		position: absolute;
		top: 50%;
		right: -100%;
		transform: translate(-8px, -50%);
		right: 0;

		.close-x {
			transform: translate(-1px, 0);
		}
	}
}
</style>
